<template>
  <div ref="root" class="filter-dropdown" :style="{ width: width + 'px' }">
    <button type="button" class="filter-trigger" :class="{ open: isOpen, active: modelValue.length > 0 }"
      @click="isOpen = !isOpen">
      <span class="filter-trigger-text">{{ triggerText }}</span>
      <svg class="chevron" :class="{ rotated: isOpen }" width="24" height="24" viewBox="0 0 24 24" fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <div v-if="isOpen" class="filter-menu">
      <div class="filter-menu-title">Filter by {{ label }}</div>
      <label class="filter-option">
        <input type="checkbox" :checked="allSelected" @change="toggleAll" />
        <span>{{ allLabel }}</span>
      </label>
      <label v-for="option in options" :key="option.value" class="filter-option">
        <input type="checkbox" :checked="modelValue.includes(option.value)" @change="toggleOption(option.value)" />
        <span>{{ option.text }}</span>
      </label>
    </div>
  </div>
</template>

<script>
export default {
  name: "FilterDropdown",
  emits: ["update:modelValue"],
  props: {
    label: {
      type: String,
      required: true,
    },
    allLabel: {
      type: String,
      default: "All",
    },
    options: {
      type: Array,
      required: true,
    },
    // Selected values; empty array means no filter (all)
    modelValue: {
      type: Array,
      default: () => [],
    },
    width: {
      type: Number,
      default: 140,
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    allSelected() {
      return this.modelValue.length === this.options.length;
    },
    triggerText() {
      if (this.modelValue.length === 0 || this.allSelected) return this.label;
      if (this.modelValue.length === 1) {
        const option = this.options.find((o) => o.value === this.modelValue[0]);
        return option ? option.text : this.label;
      }
      return `${this.label} (${this.modelValue.length})`;
    },
  },
  methods: {
    toggleAll() {
      this.$emit("update:modelValue", this.allSelected ? [] : this.options.map((o) => o.value));
    },
    toggleOption(value) {
      const next = this.modelValue.includes(value)
        ? this.modelValue.filter((v) => v !== value)
        : [...this.modelValue, value];
      this.$emit("update:modelValue", next);
    },
    onClickOutside(event) {
      if (this.isOpen && this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.isOpen = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.onClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onClickOutside);
  },
};
</script>

<style scoped>
.filter-dropdown {
  position: relative;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
}

.filter-trigger {
  width: 100%;
  height: 32px;
  padding: 0 8px 0 16px;
  border-radius: 100px;
  border: 1px solid #d0d5dd;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #667085;
  font-size: 14px;
  cursor: pointer;
}

.filter-trigger.open {
  border-color: #43bb75;
}

.filter-trigger.active {
  color: #022326;
}

.filter-trigger-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.chevron.rotated {
  transform: rotate(180deg);
}

.filter-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1001;
  min-width: 200px;
  padding: 8px 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.12);
}

.filter-menu-title {
  padding: 4px 16px;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
  border-bottom: 1px solid #eaecf0;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 16px;
  margin: 0;
  font-size: 14px;
  color: #022326;
  cursor: pointer;
  border-bottom: 1px solid #eaecf0;
}

.filter-option:last-child {
  border-bottom: none;
}

.filter-option:hover {
  background: #f9fafb;
}

.filter-option input {
  width: 18px;
  height: 18px;
  margin: 0 3px;
  accent-color: #1570ef;
  cursor: pointer;
}
</style>
